// Sugalarm — inline editor for every active bolus contributing to IOB
// (cycle 43).
//
// Cycle 42 made the Now-screen IOB ring tappable but only opened a
// read-only <ViewEntryModal> on the *newest* bolus and forced a
// navigation to /log to actually change anything. For a quick stacking
// correction the user wants to stay on the Now screen and see/edit
// *every* bolus inside the IOB window (≤4h) in one place. This modal
// lists all of them (not capped at the 2-ring visual budget), shows
// per-row units / notes / time-remaining, and edits each row in place
// — Save PATCHes via the cycle-34.20 endpoint, the cycle-24 `treatment`
// WS frame broadcasts the upsert, and the IOB ring recomputes within
// ~1 s. Delete routes through cycle 42's <ConfirmDeleteModal>, which
// the parent (NowScreen) sibling-mounts over the same backdrop.
//
// Same animation pattern as cycle-34.20 EditTreatmentModal: fixed
// blurred backdrop, 220 ms scale-in, tap-outside + Escape dismiss.
//
// Editing a bolus's `timestamp` is out of scope — same precedent as
// cycle 34.20's EditTreatmentModal. The cycle-42 `Edit in Log ›`
// handoff (the only way to change a bolus timestamp) is retired this
// cycle, so timestamp edits are explicitly unsupported here too; the
// user goes to /log directly via the nav for that.

import { type FocusEvent as ReactFocusEvent, useEffect, useRef, useState } from "react";

import * as treatmentsApi from "../../api/treatments";
import { FoodGlyph } from "../glucose/FoodGlyph";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { fmtTime } from "../../lib/glucose";
import {
  formatBasalHoursLeft,
  groupBolusesByMeal,
  insulinRowRemainingMs,
  NO_CARBS_LABEL,
  type BolusMealGroup,
} from "../../lib/iob";
import type { Treatment, TreatmentUpdateIn } from "../../lib/types";
import { Pill } from "./Pill";
import styles from "./ActiveBolusesModal.module.css";

interface Props {
  /** Active-IOB insulin rows, already filtered + newest-first by the
   *  caller (NowScreen's `activeBolusTreatments` memo). */
  boluses: Treatment[];
  /** All treatment rows — so each bolus row can show the meal it covers
   *  (the food name lives on a separate carbs row, not on the bolus). */
  treatments: Treatment[];
  /** From the parent's useNow tick — the rings share the same value so
   *  the per-row time-remaining stays in lockstep. */
  now: number;
  /** Display units, reserved for future BG-aware copy. Insulin rows
   *  are unitless on the wire. */
  units: "mg/dL" | "mmol/L";
  /** 24-hour clock preference. Falls back to 12-hour. */
  h24: boolean;
  /** Explicit open flag (not the cycle-34.22 `treatment != null`
   *  convention) so an empty list still opens cleanly during a
   *  delete-the-last-bolus race. */
  open: boolean;
  onClose: () => void;
  /** Opens cycle-42's <ConfirmDeleteModal> in the parent — this modal
   *  does NOT own the confirm dialog; NowScreen sibling-mounts both
   *  over the same backdrop. */
  onRequestDelete: (t: Treatment) => void;
  /** Cycle 45: gates the kiosk-only layout shift that lifts the modal
   *  clear of the on-screen keyboard. Web/mobile keep the cycle-43
   *  centered layout untouched (the cycle-14 keyboard is kiosk-only). */
  variant: FrameVariant;
}

interface Edit {
  units: string;
  notes: string;
}

function unitsStr(t: Treatment): string {
  return t.insulin_u == null ? "" : String(t.insulin_u);
}

// Cycle 45: must agree EXACTLY with the cycle-14 OnScreenKeyboard's
// own `isTextEditable` so the modal only treats the keyboard as
// "open" for the same fields the keyboard actually appears for. The
// units field is <input type="number"> → NOT in this set → it never
// flips `keyboardOpen` (and the cycle-14 keyboard never opens for it
// either). Only the notes field (<input type="text">) does.
const TEXT_INPUT_TYPES = new Set([
  "text",
  "email",
  "password",
  "search",
  "tel",
  "url",
]);

function isTextEditable(el: EventTarget | Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) {
    if (el.readOnly || el.disabled) return false;
    return TEXT_INPUT_TYPES.has(el.type.toLowerCase());
  }
  return false;
}

/** Cycle 123: bolus time-left badge from a remaining-ms figure. */
function fmtBolusRemaining(ms: number): string {
  const min = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m left` : `${m}m left`;
}

/** Cycle 123: the per-row time-left badge, kind-aware. Both branches read
 *  the shared `insulinRowRemainingMs`, so a basal row shows the same
 *  long-acting figure the front-card BasalLine shows (e.g. "≈23h left")
 *  and a bolus row is unchanged ("{H}h {M}m left"). */
function rowBadge(t: Treatment, now: number): string {
  const ms = insulinRowRemainingMs(t, now);
  if (t.insulin_kind === "basal") {
    return formatBasalHoursLeft(ms / 3_600_000, ms <= 0);
  }
  return fmtBolusRemaining(ms);
}

/** Cycle 123: basal label units (whole or one decimal), mirroring BasalLine. */
function fmtUnits(u: number | null): string {
  if (u == null) return "";
  const n = Number(u);
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
}

/** A row is dirty when its local map differs from the *current* props
 *  treatment values. Units compare numerically (so "3.50" === 3.5
 *  after the WS round-trip clears the flag); notes compare trimmed. */
function isDirty(t: Treatment, e: Edit): boolean {
  const srcU = t.insulin_u == null ? NaN : Number(t.insulin_u);
  const trimmed = e.units.trim();
  const editU = trimmed === "" ? NaN : Number(trimmed);
  const unitsDirty =
    Number.isNaN(srcU) && Number.isNaN(editU) ? false : editU !== srcU;
  const notesDirty = e.notes.trim() !== (t.notes ?? "").trim();
  return unitsDirty || notesDirty;
}

/** True when the inbound (props) treatment diverges from the value we
 *  originally seeded the local map from — i.e. a partner device wrote
 *  this row while the user had it open. Re-seeded on our own Save so
 *  this only ever fires for *external* writes. */
function divergedFromSeed(t: Treatment, s: Edit): boolean {
  const seedU = s.units.trim() === "" ? NaN : Number(s.units);
  const tU = t.insulin_u == null ? NaN : Number(t.insulin_u);
  const uDiff =
    Number.isNaN(seedU) && Number.isNaN(tU) ? false : seedU !== tU;
  const nDiff = s.notes.trim() !== (t.notes ?? "").trim();
  return uDiff || nDiff;
}

export function ActiveBolusesModal({
  boluses,
  treatments,
  now,
  h24,
  open,
  onClose,
  onRequestDelete,
  variant,
}: Props) {
  const kiosk = variant === "kiosk";
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  // Parallel "originally seeded" baseline per row — never updated from
  // incoming props for an existing id (only on first appearance and on
  // our own successful Save), so it's a stable anchor for the
  // "Updated elsewhere" check.
  const [seeded, setSeeded] = useState<Record<string, Edit>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [savedFlash, setSavedFlash] = useState<Record<string, boolean>>({});

  const [mounted, setMounted] = useState<boolean>(open);
  const visible = open;

  // Cycle 45: on the kiosk, a focused notes field summons the cycle-14
  // OnScreenKeyboard (z-index 400, lifted this cycle so it sits above
  // this modal's 356 backdrop). When it's up we lift the modal into
  // the upper portion of the screen so no part of the panel hides
  // behind the keyboard. Web/mobile never set this true (the cycle-14
  // keyboard is kiosk-only) so their cycle-43 layout is byte-identical.
  const [keyboardOpen, setKeyboardOpen] = useState<boolean>(false);
  const blurTimer = useRef<number | null>(null);
  const scrollTimer = useRef<number | null>(null);
  // Cycle 45 fix: true only while a press that STARTED on the backdrop
  // itself is in flight. The cycle-45 kiosk relayout (centering →
  // flex-start on notes-field focus) moves the panel out from under
  // the finger between pointerdown and pointerup, so the synthesized
  // `click` retargets to the backdrop — which would otherwise dismiss
  // the modal the instant the keyboard comes up. Closing only when the
  // press *originated* on the backdrop also hardens the general
  // "select text in the notes field, release outside" case.
  const pressOnBackdrop = useRef<boolean>(false);
  const kbActive = kiosk && keyboardOpen && open;

  // Reset when the modal closes so a re-open starts centered.
  useEffect(() => {
    if (!open) setKeyboardOpen(false);
  }, [open]);

  useEffect(
    () => () => {
      if (blurTimer.current != null) window.clearTimeout(blurTimer.current);
      if (scrollTimer.current != null) window.clearTimeout(scrollTimer.current);
    },
    [],
  );

  // focusin / focusout bubble through React's onFocus / onBlur on the
  // modal root. The cycle-14 keyboard listens on `document` for the
  // same focus events on its own scope — both fire independently; we
  // deliberately do NOT stopPropagation so the keyboard still opens.
  const onModalFocus = (e: ReactFocusEvent<HTMLDivElement>): void => {
    if (!kiosk || !isTextEditable(e.target)) return;
    const tgt = e.target as HTMLElement;
    if (blurTimer.current != null) {
      window.clearTimeout(blurTimer.current);
      blurTimer.current = null;
    }
    setKeyboardOpen(true);
    // Let the modal re-layout + the keyboard slide-up (200 ms) settle,
    // then scroll the focused row into view *inside the modal's own
    // scroll container* (block:"nearest" walks the nearest scrollable
    // ancestor — the modal body — not the page). Mirrors cycle-14.
    if (scrollTimer.current != null) window.clearTimeout(scrollTimer.current);
    scrollTimer.current = window.setTimeout(() => {
      try {
        tgt.scrollIntoView({ block: "nearest", behavior: "smooth" });
      } catch {
        /* older engines — best effort */
      }
    }, 250);
  };

  const onModalBlur = (): void => {
    if (!kiosk) return;
    // 100 ms grace so tabbing between the units and notes fields
    // doesn't flash the layout (matches the cycle-14 keyboard's own
    // tolerance for focus moving between editable siblings).
    if (blurTimer.current != null) window.clearTimeout(blurTimer.current);
    blurTimer.current = window.setTimeout(() => {
      blurTimer.current = null;
      if (!isTextEditable(document.activeElement)) setKeyboardOpen(false);
    }, 100);
  };

  // Seed any newly-appeared id; prune ids that left the IOB window.
  // A fresh bolus logged while the modal is open re-seeds *that row
  // only* — existing rows the user is mid-edit on are never clobbered.
  useEffect(() => {
    const ids = new Set(boluses.map((b) => b.id));
    const seedNew = (prev: Record<string, Edit>) => {
      const next = { ...prev };
      let changed = false;
      for (const b of boluses) {
        if (next[b.id] === undefined) {
          next[b.id] = { units: unitsStr(b), notes: b.notes ?? "" };
          changed = true;
        }
      }
      for (const id of Object.keys(next)) {
        if (!ids.has(id)) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    };
    setEdits(seedNew);
    setSeeded(seedNew);
  }, [boluses]);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      return;
    }
    const t = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(t);
  }, [visible]);

  // Escape dismisses on kiosk + web where a physical keyboard exists.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [visible, onClose]);

  const flashTimers = useRef<Record<string, number>>({});
  useEffect(() => {
    const timers = flashTimers.current;
    return () => {
      for (const id of Object.keys(timers)) window.clearTimeout(timers[id]);
    };
  }, []);

  function flashSaved(id: string): void {
    setSavedFlash((p) => ({ ...p, [id]: true }));
    if (flashTimers.current[id]) {
      window.clearTimeout(flashTimers.current[id]);
    }
    flashTimers.current[id] = window.setTimeout(() => {
      setSavedFlash((p) => ({ ...p, [id]: false }));
      delete flashTimers.current[id];
    }, 1600);
  }

  function patchRow(id: string, patch: Partial<Edit>): void {
    setEdits((p) => ({ ...p, [id]: { ...p[id]!, ...patch } }));
  }

  function resetRow(t: Treatment): void {
    // "source values" = the row's *current* props treatment. This
    // doubles as the conflict escape hatch — Reset adopts a partner's
    // external change.
    setEdits((p) => ({
      ...p,
      [t.id]: { units: unitsStr(t), notes: t.notes ?? "" },
    }));
    setErrors((p) => ({ ...p, [t.id]: null }));
  }

  async function saveRow(t: Treatment): Promise<void> {
    const e = edits[t.id];
    if (!e) return;
    setErrors((p) => ({ ...p, [t.id]: null }));

    const trimmed = e.units.trim();
    const n = Number(trimmed);
    if (trimmed === "" || !Number.isFinite(n) || n <= 0) {
      setErrors((p) => ({
        ...p,
        [t.id]: "Enter a valid number of units.",
      }));
      return;
    }

    // Cycle-34.20 contract: only `insulin_u` + `notes` on this row,
    // and only the fields that actually changed. Never touch
    // event_type / timestamp / provenance / entered_by.
    const body: TreatmentUpdateIn = {};
    if (n !== Number(t.insulin_u)) body.insulin_u = n;
    const trimmedNotes = e.notes.trim();
    if (trimmedNotes !== (t.notes ?? "").trim()) {
      body.notes = trimmedNotes.length > 0 ? trimmedNotes : null;
    }
    if (body.insulin_u === undefined && body.notes === undefined) {
      // Nothing materially changed (e.g. "3.50" → 3.5). Acknowledge
      // without a redundant write.
      flashSaved(t.id);
      return;
    }

    setSaving((p) => ({ ...p, [t.id]: true }));
    const r = await treatmentsApi.update(t.id, body);
    setSaving((p) => ({ ...p, [t.id]: false }));
    if (!r.ok) {
      setErrors((p) => ({ ...p, [t.id]: r.error.message }));
      return;
    }
    // Re-seed both maps from the authoritative server row so the dirty
    // flag clears and the "Updated elsewhere" baseline resets. The
    // cycle-24 `treatment` WS frame carries the same values within
    // ~100 ms — no refetch. Do NOT close: the user may be saving
    // across several stacked boluses.
    const fresh: Edit = {
      units: unitsStr(r.data),
      notes: r.data.notes ?? "",
    };
    setEdits((p) => ({ ...p, [t.id]: fresh }));
    setSeeded((p) => ({ ...p, [t.id]: fresh }));
    flashSaved(t.id);
  }

  if (!mounted) return null;

  // Cycle 123: the editable row (units / notes / time-left badge / clock /
  // Save·Reset·× cluster) — shared by bolus rows (under their meal group
  // header) and basal rows (under an "L" / long-acting header). The badge
  // is kind-aware via `rowBadge`.
  const renderEditorRow = (t: Treatment) => {
    const e = edits[t.id] ?? { units: unitsStr(t), notes: t.notes ?? "" };
    const dirty = isDirty(t, e);
    const s = seeded[t.id];
    const elsewhere = dirty && s != null && divergedFromSeed(t, s);
    const isSaving = saving[t.id] === true;
    const err = errors[t.id] ?? null;
    return (
      <div key={t.id} className={styles.row}>
        <div className={styles.unitsWrap}>
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            value={e.units}
            onChange={(ev) => patchRow(t.id, { units: ev.target.value })}
            aria-label="Units"
            className={styles.unitsInput}
          />
          <span className={styles.unitsSuffix}>u</span>
        </div>

        <input
          type="text"
          value={e.notes}
          onChange={(ev) => patchRow(t.id, { notes: ev.target.value })}
          maxLength={200}
          placeholder="Bolus, basal, correction…"
          aria-label="Notes"
          className={styles.notesInput}
        />

        <div className={styles.timeCol}>
          <span className={styles.timeBadge}>{rowBadge(t, now)}</span>
          <span className={styles.timeClock}>{fmtTime(t.timestamp, { h24 })}</span>
        </div>

        <div className={styles.actions}>
          {elsewhere && (
            <span className={styles.updatedElsewhere}>Updated elsewhere</span>
          )}
          {savedFlash[t.id] && <Pill tone="ok">Saved</Pill>}
          <button
            type="button"
            onClick={() => void saveRow(t)}
            disabled={!dirty || isSaving}
            className={`${styles.btn} ${
              dirty ? styles.btnSaveDirty : styles.btnSaveClean
            }`}
          >
            {isSaving ? "Saving…" : "Save"}
          </button>
          {dirty && (
            <button
              type="button"
              onClick={() => resetRow(t)}
              disabled={isSaving}
              className={`${styles.btn} ${styles.btnReset}`}
            >
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={() => onRequestDelete(t)}
            aria-label="Delete insulin row"
            className={`${styles.btn} ${styles.btnDelete}`}
          >
            ×
          </button>
        </div>

        {err && <div className={styles.rowError}>{err}</div>}
      </div>
    );
  };

  // Cycle 123: basal rows are pulled OUT of the meal grouping (they're not
  // meals/corrections) and rendered as their own long-acting rows. Bolus
  // groups + basal rows are ordered newest-first by their newest dose.
  type ModalItem =
    | { kind: "group"; at: number; group: BolusMealGroup }
    | { kind: "basal"; at: number; row: Treatment };
  const bolusRows = boluses.filter((t) => t.insulin_kind !== "basal");
  const basalRows = boluses.filter((t) => t.insulin_kind === "basal");
  const items: ModalItem[] = [
    ...groupBolusesByMeal(bolusRows, treatments, now).map((g) => ({
      kind: "group" as const,
      at: Math.max(...g.boluses.map((b) => new Date(b.timestamp).getTime())),
      group: g,
    })),
    ...basalRows.map((r) => ({
      kind: "basal" as const,
      at: new Date(r.timestamp).getTime(),
      row: r,
    })),
  ];
  items.sort((a, b) => b.at - a.at);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Active boluses"
      onMouseDown={(e) => {
        pressOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // Only dismiss when the press both ENDED and STARTED on the
        // bare backdrop. Guards against the cycle-45 relayout
        // retargeting a notes-field tap's click onto the backdrop.
        if (e.target === e.currentTarget && pressOnBackdrop.current) {
          onClose();
        }
      }}
      onFocus={onModalFocus}
      onBlur={onModalBlur}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 356,
        display: "flex",
        // Cycle 45: when the kiosk keyboard is up, pin the modal to
        // the top of the viewport instead of centering it so its
        // bottom edge clears the keyboard footprint entirely.
        alignItems: kbActive ? "flex-start" : "center",
        justifyContent: "center",
        background: visible ? "rgba(0, 0, 0, 0.32)" : "transparent",
        backdropFilter: "blur(3px)",
        transition: "background 220ms ease-out",
        pointerEvents: visible ? "auto" : "none",
        padding: kbActive ? "16px 16px 0" : 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(480px, 100%)",
          // Cycle 45: shrink the scroll container when the kiosk
          // keyboard is up so the focused row stays visible above it
          // (the body's flex:1/overflow-y:auto already scrolls).
          maxHeight: kbActive
            ? "min(680px, calc(100vh - 320px))"
            : "min(680px, calc(100vh - 32px))",
          background: "var(--bg-elev)",
          border: "0.5px solid var(--line-strong)",
          borderRadius: 18,
          boxShadow: "0 16px 48px rgba(0, 0, 0, 0.4)",
          transform: visible
            ? "scale(1) translateY(0)"
            : "scale(0.94) translateY(8px)",
          opacity: visible ? 1 : 0,
          transition: "transform 220ms ease-out, opacity 200ms ease-out",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div
          style={{
            flexShrink: 0,
            padding: "20px 22px 12px",
            borderBottom: "0.5px solid var(--line)",
          }}
        >
          <span
            style={{
              fontSize: "var(--type-floor)",
              fontWeight: 700,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: "var(--accent)",
            }}
          >
            Insulin on board · {boluses.length} active
          </span>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "4px 22px",
          }}
        >
          {boluses.length === 0 ? (
            <div
              style={{
                padding: "28px 0",
                textAlign: "center",
                fontSize: 14,
                color: "var(--ink-3)",
              }}
            >
              No active insulin.
            </div>
          ) : (
            items.map((item) => {
              // Cycle 123: a basal dose — its own long-acting row, led by an
              // "L" badge + "{label} · long-acting · {units}u", mirroring the
              // front-card BasalLine. Same editable row beneath it.
              if (item.kind === "basal") {
                const r = item.row;
                return (
                  <div key={`basal-${r.id}`} className={styles.mealGroup}>
                    <div className={styles.basalHeader}>
                      <span className={styles.basalBadge} aria-hidden>
                        L
                      </span>
                      <span className={styles.basalLabel}>
                        {`${r.insulin_label ?? "Basal"} · long-acting · ${fmtUnits(
                          r.insulin_u,
                        )}u`}
                      </span>
                    </div>
                    {renderEditorRow(r)}
                  </div>
                );
              }
              const group = item.group;
              const ctx = group.context;
              return (
                <div key={group.key} className={styles.mealGroup}>
                  {/* Cycle 75a hotfix: the meal context renders ONCE as a
                      read-only header above the group's bolus rows —
                      removing the per-row duplication that repeated the
                      food name + carbs once per stacked bolus. */}
                  {ctx.isCorrection ? (
                    <div className={styles.correctionHeader}>
                      <span className={styles.ctxDotCorrection} />
                      <span className={styles.correctionLabel}>Correction</span>
                    </div>
                  ) : ctx.food == null && ctx.carbsG == null ? (
                    // Cycle 104c: a forced meal-type dose (the first dose)
                    // with no meal context renders as a compact blue
                    // "Bolus" header — never the amber Correction, never a
                    // blank "Meal" header.
                    <div className={styles.correctionHeader}>
                      <span className={styles.ctxDotMeal} />
                      <span className={styles.correctionLabel}>Bolus</span>
                    </div>
                  ) : (
                    <div className={styles.mealHeader}>
                      <span className={styles.ctxDotMeal} />
                      <span className={styles.ctxGlyph} aria-hidden>
                        <FoodGlyph iconKey={ctx.foodIconKey} size={14} />
                      </span>
                      {ctx.food != null ? (
                        <span className={styles.ctxFood}>{ctx.food}</span>
                      ) : (
                        <span className={styles.ctxFoodPlaceholder}>Meal</span>
                      )}
                      {ctx.carbsG != null && (
                        <span className={styles.ctxCarbs}>
                          {ctx.carbsG === 0
                            ? NO_CARBS_LABEL
                            : `Estimated ~${Math.round(ctx.carbsG)}g carbs`}
                        </span>
                      )}
                    </div>
                  )}
                  {group.boluses.map(renderEditorRow)}
                </div>
              );
            })
          )}
        </div>

        <div
          style={{
            flexShrink: 0,
            padding: "12px 22px 18px",
            borderTop: "0.5px solid var(--line)",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              appearance: "none",
              border: "0.5px solid var(--line-strong)",
              background: "transparent",
              color: "var(--ink-2)",
              borderRadius: 999,
              padding: "8px 20px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
